<template>
  <div class="inspection-point-container">
    <el-row :gutter="16">
      <!-- 左侧巡检点导航 -->
      <el-col :xs="24" :sm="24" :md="6" :lg="5">
        <div class="ds-nav-card">
          <div class="ds-nav-card__header">{{ $t('inspectionPoint.page.listTitle') }}</div>
          <div class="ds-nav-card__filter">
            <el-input v-model="inspectionPointInfo.conditions.inspectionName" clearable class="w-full"
              :placeholder="$t('inspectionPoint.placeholder.inspectionName')"
              @keyup.enter.native="_queryInspectionPointMethod()" />
            <div class="ds-nav-card__filter-actions">
              <el-button type="text" icon="el-icon-search" @click="_queryInspectionPointMethod()">
                {{ $t('common.search') }}
              </el-button>
              <el-button type="text" icon="el-icon-plus" @click="_openAddInspectionPointModal()">
                {{ $t('common.add') }}
              </el-button>
            </div>
          </div>
          <el-scrollbar class="ds-nav-card__scroll">
            <ul class="ds-nav-card__list">
              <li v-for="(point, index) in inspectionPointInfo.points" :key="index" class="ds-nav-card__item"
                :class="{ 'is-active': point.inspectionId == inspectionPointInfo.point.inspectionId }"
                @click="_switchInspectionPoint(point)">
                <i class="el-icon-location"></i>
                <span class="ds-nav-card__name">{{ point.inspectionName }}</span>
              </li>
            </ul>
          </el-scrollbar>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="18" :lg="19">
        <template v-if="inspectionPointInfo.point.inspectionId">
          <!-- (1) PageHeader -->
          <ds-page-header :title="$t('inspectionPoint.title')" :subtitle="inspectionPointInfo.point.inspectionName">
            <template #extra>
              <el-button icon="el-icon-edit" @click="_openEditInspectionPointModel(inspectionPointInfo.point)">
                {{ $t('common.edit') }}
              </el-button>
              <el-button icon="el-icon-delete" class="ds-btn-danger" @click="_openDeleteInspectionPointModel(inspectionPointInfo.point)">
                {{ $t('common.delete') }}
              </el-button>
              <el-button type="primary" icon="el-icon-camera" @click="_pointQrCode(inspectionPointInfo.point)">
                {{ $t('inspectionPoint.qrCode') }}
              </el-button>
            </template>
          </ds-page-header>

          <!-- 详情信息面板 -->
          <div class="ds-panel ds-panel--detail">
            <el-row :gutter="16">
              <el-col :xs="24" :sm="12" :md="8" :lg="4">
                <div class="ds-field">
                  <div class="ds-field__label">{{ $t('inspectionPoint.inspectionPoint') }}</div>
                  <div class="ds-field__value">{{ inspectionPointInfo.point.inspectionName || '-' }}</div>
                </div>
              </el-col>
              <el-col :xs="24" :sm="12" :md="8" :lg="4">
                <div class="ds-field">
                  <div class="ds-field__label">{{ $t('inspectionPoint.inspectionType') }}</div>
                  <div class="ds-field__value">{{ inspectionPointInfo.point.pointTypeName || '-' }}</div>
                </div>
              </el-col>
              <el-col :xs="24" :sm="12" :md="8" :lg="4">
                <div class="ds-field">
                  <div class="ds-field__label">{{ $t('inspectionPoint.inspectionLocation') }}</div>
                  <div class="ds-field__value">{{ inspectionPointInfo.point.pointObjName || '-' }}</div>
                </div>
              </el-col>
              <el-col :xs="24" :sm="12" :md="8" :lg="4">
                <div class="ds-field">
                  <div class="ds-field__label">{{ $t('inspectionPoint.inspectionItem') }}</div>
                  <div class="ds-field__value">{{ inspectionPointInfo.point.itemName || '-' }}</div>
                </div>
              </el-col>
              <el-col :xs="24" :sm="12" :md="8" :lg="4">
                <div class="ds-field">
                  <div class="ds-field__label">NFC</div>
                  <div class="ds-field__value ds-num">{{ inspectionPointInfo.point.nfcCode || '-' }}</div>
                </div>
              </el-col>
            </el-row>
          </div>

          <!-- 标签页面板 -->
          <div class="ds-panel ds-panel--tabs">
            <el-tabs v-model="inspectionPointInfo._currentTab" @tab-click="changeTab(inspectionPointInfo._currentTab)">
              <el-tab-pane name="pointTaskDetail" :label="$t('inspectionPoint.tabs.pointTaskDetail')">
                <point-task-detail v-if="inspectionPointInfo._currentTab === 'pointTaskDetail'"
                  ref="pointTaskDetail" />
              </el-tab-pane>
              <el-tab-pane name="pointRoute" :label="$t('inspectionPoint.tabs.pointRoute')">
                <point-route v-if="inspectionPointInfo._currentTab === 'pointRoute'" ref="pointRoute" />
              </el-tab-pane>
              <el-tab-pane name="pointPlan" :label="$t('inspectionPoint.tabs.pointPlan')">
                <point-plan v-if="inspectionPointInfo._currentTab === 'pointPlan'" ref="pointPlan" />
              </el-tab-pane>
              <el-tab-pane name="pointTask" :label="$t('inspectionPoint.tabs.pointTask')">
                <point-task v-if="inspectionPointInfo._currentTab === 'pointTask'" ref="pointTask" />
              </el-tab-pane>
            </el-tabs>
          </div>
        </template>

        <!-- 未选中巡检点时的空态 -->
        <div v-else class="ds-panel ds-panel--empty">
          <ds-empty-state :description="$t('inspectionPoint.page.empty')" />
        </div>
      </el-col>
    </el-row>

    <!-- 模态框组件 -->
    <add-inspection-point ref="addInspectionPoint" @success="_listInspectionPoints" />
    <edit-inspection-point ref="editInspectionPoint" @success="_listInspectionPoints" />
    <delete-inspection-point ref="deleteInspectionPoint" @success="_listInspectionPoints" />
    <inspection-point-qr-code ref="inspectionPointQrCode" @success="_listInspectionPoints" />
  </div>
</template>

<script>
import { listInspectionPoints } from '@/api/inspection/inspectionPointApi'
import { getDict } from '@/api/community/communityApi'
import { getCommunityId } from '@/api/community/communityApi'
import PointTaskDetail from '@/components/inspection/pointTaskDetail'
import PointRoute from '@/components/inspection/pointRoute'
import PointPlan from '@/components/inspection/pointPlan'
import PointTask from '@/components/inspection/pointTask'
import AddInspectionPoint from '@/components/inspection/addInspectionPoint'
import EditInspectionPoint from '@/components/inspection/editInspectionPoint'
import DeleteInspectionPoint from '@/components/inspection/deleteInspectionPoint'
import InspectionPointQrCode from '@/components/inspection/inspectionPointQrCode'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsEmptyState from '@/components/ds/ds-empty-state.vue'

export default {
  name: 'InspectionPointList',
  components: {
    PointTaskDetail,
    PointRoute,
    PointPlan,
    PointTask,
    AddInspectionPoint,
    EditInspectionPoint,
    DeleteInspectionPoint,
    InspectionPointQrCode,
    DsPageHeader,
    DsEmptyState
  },
  data() {
    return {
      inspectionPointInfo: {
        points: [],
        point: {},
        total: 0,
        records: 1,
        moreCondition: false,
        inspectionName: '',
        pointObjTypes: [],
        _currentTab: 'pointTaskDetail',
        conditions: {
          inspectionId: '',
          machineId: '',
          inspectionName: '',
          machineCode: '',
          pointObjType: '',
          communityId: '',
          page: 1,
          row: 1000
        }
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.inspectionPointInfo.conditions.communityId = this.communityId
    this.getDictData()
    this._listInspectionPoints()
  },
  methods: {
    async getDictData() {
      try {
        const data = await getDict('inspection_point', 'point_obj_type')
        this.inspectionPointInfo.pointObjTypes = data
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    async _listInspectionPoints() {
      try {
        const response = await listInspectionPoints(this.inspectionPointInfo.conditions)
        this.inspectionPointInfo.points = Array.isArray(response.inspectionPoints) ? response.inspectionPoints : []
        this.inspectionPointInfo.total = response.total
        this.inspectionPointInfo.records = response.records

        if (response.inspectionPoints && response.inspectionPoints.length > 0) {
          this._switchInspectionPoint(response.inspectionPoints[0])
        }
      } catch (error) {
        console.error('获取巡检点列表失败:', error)
      }
    },
    _queryInspectionPointMethod() {
      this.inspectionPointInfo.conditions.page = 1
      this._listInspectionPoints()
    },
    _switchInspectionPoint(point) {
      this.inspectionPointInfo.point = point
      this.changeTab(this.inspectionPointInfo._currentTab)
    },
    changeTab(tab) {
      this.inspectionPointInfo._currentTab = tab

      // 触发子组件加载数据
      setTimeout(() => {
        if (this.$refs[tab]) {
          this.$refs[tab].loadData(this.inspectionPointInfo.point)
        }
      }, 500)

    },
    _openAddInspectionPointModal() {
      this.$refs.addInspectionPoint.open()
    },
    _openEditInspectionPointModel(point) {
      this.$refs.editInspectionPoint.open(point)
    },
    _openDeleteInspectionPointModel(point) {
      this.$refs.deleteInspectionPoint.open(point)
    },
    _pointQrCode(point) {
      console.log(point)
      this.$refs.inspectionPointQrCode.open(point)
    }
  }
}
</script>

<style lang="scss" scoped>
.inspection-point-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧巡检点导航卡片 */
.ds-nav-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__header {
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__filter {
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);

    &-actions {
      display: flex;
      align-items: center;
      gap: var(--ds-spacing-xs);
      margin-top: var(--ds-spacing-xs);
    }
  }

  &__scroll {
    max-height: calc(100vh - 340px);
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: var(--ds-spacing-sm);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-sm);
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-xs);
    border-radius: var(--ds-radius-sm);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    cursor: pointer;
    transition: background-color var(--ds-transition-fast),
      color var(--ds-transition-fast);

    &:hover {
      background-color: rgba(var(--ds-primary-rgb), 0.08);
      color: var(--ds-primary);
    }

    &.is-active {
      background-color: var(--ds-primary);
      color: #fff;
      font-weight: 600;
    }
  }

  &__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* 白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &--detail {
    padding: var(--ds-spacing-lg);
    margin-bottom: var(--ds-spacing-md);
  }

  &--tabs {
    padding: 0 var(--ds-spacing-lg) var(--ds-spacing-md);
  }

  &--empty {
    padding: var(--ds-spacing-lg);
  }
}

/* 详情字段 */
.ds-field {
  &__label {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
    margin-bottom: var(--ds-spacing-xs);
  }

  &__value {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-800);
    font-weight: 500;
    word-break: break-all;
  }
}

/* 危险操作按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
